/**
 * トップ（`/`）の県選び画面。
 *
 * 47県ある以上、トップでいきなり1県が立ち上がると「その県のゲーム」に見えてしまう。
 * ここでは Phaser も県の画像も読み込まず、県の一覧だけを出す。
 * そのぶん初回の表示が速く、記事から来た人が最初に見る画面としても素直になる。
 *
 * 県をえらぶと `location.assign()` で通常の遷移をする。
 * ステージの張り替えより読み込み直すほうが確実で、URL が素直に変わるぶん
 * 共有も解析（Cloudflare Web Analytics はパスを記録する）も正しくなる。
 */

import { AUDIO_CREDITS } from '../game/config/audio';
import { metaStorageKey, settingsStorageKey } from '../game/config/gameConfig';
import { STAGES, stagePath } from '../game/config/stages';
import { DailyBook } from '../meta/daily';
import { RankingIdentityStore } from '../meta/dailyRanking';
import { todaysDaily } from '../meta/dailyStage';
import { RankingModal } from './RankingModal';
import type { Platform } from '../platform/Platform';
import type { TrophyBook } from '../meta/progress';
import { MEDAL_LABEL, medalOf, type PrefMedal } from '../meta/trophies';

import {
  clearRate,
  collectStageSummaries,
  hasPlayed,
  scoreLabel,
  type StageSummary,
} from './StageSelectModal';
import { isLargeText, setLargeTextInStorage } from './inputMode';
import { TILE_COLUMNS, TILE_POSITIONS, TILE_ROWS } from './japanTileMap';
import { groupByArea } from './stageGrouping';
import { TrophyShelf, renderTrophyBadge } from './TrophyShelf';
import { VisitedStore, decorateVisitedRows, markVisitedTiles } from './visitedMarks';
import { storeLinksForWeb } from '../platform/storeLinks';
import { isNativeApp } from '../platform/native';

/**
 * 収録の合計を「11道県・132品」の形にする。
 *
 * 単位は中身から作る。北海道だけのうちは「1道」、東京が入れば「都」も付く。
 * 「11県」と書くと北海道を県と呼ぶことになるので、ここは決め打ちにしない。
 */
function totals(summaries: readonly StageSummary[]): { regionLabel: string; dishes: number } {
  const target = summaries.filter((s) => !s.isLocal);
  const has = (suffix: string) => target.some((s) => s.regionName.endsWith(suffix));
  const unit = [
    has('都') && '都',
    has('北海道') && '道',
    has('府') && '府',
    has('県') && '県',
  ]
    .filter(Boolean)
    .join('');
  return {
    regionLabel: `${target.length}${unit || '地域'}`,
    dishes: target.reduce((sum, s) => sum + s.itemCount, 0),
  };
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

function buildRow(summary: StageSummary, medal: PrefMedal): HTMLElement {
  const row = el('button', 'landing-item');
  row.type = 'button';
  row.dataset.region = STAGES[summary.id]?.regionId ?? '';
  row.addEventListener('click', () => {
    location.assign(stagePath(summary.id));
  });

  const name = el('span', 'landing-item__name', summary.regionName);
  if (medal !== 'none') {
    const badge = el('span', 'medal-dot', MEDAL_LABEL[medal]);
    badge.dataset.medal = medal;
    badge.setAttribute('aria-label', `${MEDAL_LABEL[medal]}メダル`);
    name.append(badge);
  }
  row.append(
    name,
    el('span', 'landing-item__meta', `図鑑 ${summary.unlockedCount} / ${summary.itemCount}`),
    el(
      'span',
      'landing-item__score',
      scoreLabel(summary),
    ),
  );
  return row;
}

function readLastStageId(): string | null {
  try {
    const id = localStorage.getItem(metaStorageKey('last-stage'));
    return id && STAGES[id] && !STAGES[id].draft ? id : null;
  } catch {
    return null;
  }
}

/**
 * おやすみ券。昨日遊び忘れて連続が切れかけているとき、週に1回だけ守れる。
 * アプリ版はごほうび広告と引き換え、広告のない Web 版はそのまま使える。
 */
function buildRestTicket(book: DailyBook, platform: Platform | undefined, onUsed: () => void): HTMLElement | null {
  if (!book.canRest()) return null;
  const box = el('div', 'landing-rest');
  const text = el(
    'p',
    'landing-rest__text',
    '昨日は今日の県をお休みしました。おやすみ券（週に1回）を使うと、連続日数がつながったままになります。',
  );
  const monetized = platform?.monetized === true;
  const btn = el('button', 'btn btn--primary landing-rest__btn', monetized ? '▶ 広告を見ておやすみ券を使う' : '🛌 おやすみ券を使う');
  btn.type = 'button';
  btn.addEventListener('click', async () => {
    btn.disabled = true;
    const ok = monetized ? await platform!.showRewarded('rest') : true;
    if (ok && book.useRest()) {
      onUsed();
      return;
    }
    btn.disabled = false;
  });
  box.append(text, btn);
  return box;
}

/** 今日の県。全員同じ順番で落ちてくる日替わり。 */
function buildDaily(): HTMLElement | null {
  const today = todaysDaily();
  if (!today) return null;
  const view = new DailyBook().view();
  const card = el('button', 'landing-daily');
  card.type = 'button';
  card.addEventListener('click', () => location.assign(today.href));
  const text = el('span', 'landing-continue__text');
  const meta = view.playedToday
    ? `きょうのベスト ${view.bestToday.toLocaleString('ja-JP')} 点 ・ 連続 ${view.streak} 日`
    : view.streak > 0
      ? `連続 ${view.streak} 日。今日も遊ぶと ${view.streak + 1} 日に`
      : 'みんな同じ順番で料理が落ちてくる。スコアを見せ合おう';
  text.append(
    el('span', 'landing-continue__label', `今日の県 ${today.shortDate}`),
    el('span', 'landing-continue__name', today.regionName),
    el('span', 'landing-continue__meta', meta),
  );
  // 何のゲームか一目で分かるよう、今日の県の料理を小さく並べる（Lv1〜6。上位は隠しておく）
  const dishes = el('span', 'landing-daily__dishes');
  dishes.setAttribute('aria-hidden', 'true');
  for (const item of (STAGES[today.stageId]?.items ?? []).slice(0, 6)) {
    const img = el('img', 'landing-daily__dish');
    if (!item.image) continue;
    img.src = item.image;
    img.alt = '';
    img.width = 40;
    img.height = 40;
    img.loading = 'lazy';
    img.decoding = 'async';
    dishes.append(img);
  }
  text.append(dishes);
  card.append(text, el('span', 'landing-continue__go', view.playedToday ? 'もう一度 ▶' : 'あそぶ ▶'));
  return card;
}

/** 前回遊んだ県を、いちばん上に大きく出す。 */
function buildContinue(summary: StageSummary, medal: PrefMedal): HTMLElement {
  const card = el('button', 'landing-continue');
  card.type = 'button';
  card.addEventListener('click', () => location.assign(stagePath(summary.id)));
  const text = el('span', 'landing-continue__text');
  text.append(
    el('span', 'landing-continue__label', 'つづきから'),
    el('span', 'landing-continue__name', summary.regionName),
    el(
      'span',
      'landing-continue__meta',
      `図鑑 ${summary.unlockedCount} / ${summary.itemCount}` +
        (medal !== 'none' ? ` ・ ${MEDAL_LABEL[medal]}メダル` : ''),
    ),
  );
  card.append(text, el('span', 'landing-continue__go', 'あそぶ ▶'));
  return card;
}

/** 日本のタイル地図。メダルの色で塗る。 */
function buildTileMap(
  summaries: readonly StageSummary[],
  medalOfStage: (stageId: string) => PrefMedal,
): HTMLElement {
  const map = el('div', 'tile-map');
  map.setAttribute('role', 'group');
  map.setAttribute('aria-label', '日本地図から選ぶ');
  map.style.gridTemplateColumns = `repeat(${TILE_COLUMNS}, minmax(0, 1fr))`;
  map.style.gridTemplateRows = `repeat(${TILE_ROWS}, auto)`;
  for (const summary of summaries) {
    const regionId = STAGES[summary.id]?.regionId ?? '';
    const pos = TILE_POSITIONS[regionId];
    if (!pos) continue;
    const medal = medalOfStage(summary.id);
    const tile = el('button', 'tile-map__tile', summary.regionName.replace(/[都府県]$/, ''));
    tile.type = 'button';
    tile.style.gridColumn = String(pos[0] + 1);
    tile.style.gridRow = String(pos[1] + 1);
    tile.dataset.medal = medal;
    tile.dataset.played = String(hasPlayed(summary));
    tile.dataset.region = regionId;
    tile.setAttribute(
      'aria-label',
      `${summary.regionName}${medal !== 'none' ? `（${MEDAL_LABEL[medal]}メダル）` : hasPlayed(summary) ? '（あそんだ）' : ''}`,
    );
    tile.addEventListener('click', () => location.assign(stagePath(summary.id)));
    map.append(tile);
  }
  return map;
}

/** トップ画面を描く。ゲーム本体は起動しない。 */
export function renderLandingScreen(
  appRoot: HTMLElement,
  options: { trophies?: TrophyBook; platform?: Platform } = {},
): void {
  const summaries = collectStageSummaries();
  const snapshot = options.trophies?.sync();
  const medalByRegion = new Map(
    (snapshot?.progress.prefs ?? []).map((p) => [p.regionId, medalOf(p)] as const),
  );
  const medalOfStage = (stageId: string): PrefMedal =>
    medalByRegion.get(STAGES[stageId]?.regionId ?? '') ?? 'none';
  const { cleared, total } = clearRate(summaries);
  const { regionLabel, dishes } = totals(summaries);

  // 読み上げの「本文へ移動」で入れるよう main にする（2026-10-09）
  const screen = el('main', 'landing');

  const head = el('header', 'landing__head');
  head.append(
    el('h1', 'landing__title', 'ご当地めしマージ'),
    el(
      'p',
      'landing__lead',
      `同じ料理どうしをぶつけて進化させる落ちものパズル。いま${regionLabel}・料理${dishes}品で遊べます。`,
    ),
    el(
      'p',
      'landing__disclaimer',
      '個人が作った非公式のゲームです。県・市町村・施設の公認や監修は受けていません。',
    ),
  );
  const textSize = el('button', 'btn landing__textsize');
  textSize.type = 'button';
  const syncTextSize = () => {
    const large = isLargeText();
    textSize.textContent = large ? '文字を標準に' : '文字を大きく';
    textSize.setAttribute('aria-pressed', String(large));
  };
  syncTextSize();
  textSize.addEventListener('click', () => {
    setLargeTextInStorage(settingsStorageKey(), !isLargeText());
    syncTextSize();
  });
  head.append(textSize);
  // アプリ版のストアへのリンク（公開日に src/platform/storeLinks.ts へURLを入れると出る。アプリの中では出さない）
  const stores = storeLinksForWeb(isNativeApp());
  if (stores.length > 0) {
    const row = el('p', 'landing__stores');
    for (const store of stores) {
      const a = el('a', `btn landing__store landing__store--${store.os}`, store.label);
      a.href = store.url;
      a.target = '_blank';
      a.rel = 'noopener';
      row.append(a);
    }
    row.append(el('span', 'landing__stores-note', 'アプリ版でも同じ47都道府県で遊べます（Web版の記録は引き継がれません）'));
    head.append(row);
  }

  const bar = el('div', 'landing__bar');
  bar.append(
    el('span', 'landing__pick', '遊ぶ地域をえらんでください'),
    el('span', 'landing__progress', `制覇 ${cleared} / ${total}`),
  );
  if (options.trophies) {
    const book = options.trophies;
    const shelf = new TrophyShelf(book);
    const button = el('button', 'btn landing__trophy', '🏆 トロフィー棚');
    button.type = 'button';
    const achieved = snapshot?.statuses.filter((s) => s.achieved).length ?? 0;
    button.append(el('span', 'landing__trophy-count', ` ${achieved}/${snapshot?.statuses.length ?? 0}`));
    renderTrophyBadge(button, book.unseenCount);
    button.addEventListener('click', () => {
      shelf.open();
      renderTrophyBadge(button, book.unseenCount);
    });
    bar.append(button);
  }

  // 上から「つづきから」→ 日本地図 → 地方別の一覧
  const lastId = readLastStageId();
  const lastSummary = lastId ? summaries.find((s) => s.id === lastId) : undefined;
  const top: HTMLElement[] = [];
  const dailyCard = buildDaily();
  if (dailyCard) top.push(dailyCard);
  const rest = buildRestTicket(new DailyBook(), options.platform, () => renderLandingScreen(appRoot, options));
  if (rest) top.push(rest);
  const today = todaysDaily();
  if (today) {
    const rankBtn = el('button', 'btn landing-ranking', '📊 今日のランキングを見る');
    rankBtn.type = 'button';
    let modal: RankingModal | null = null;
    rankBtn.addEventListener('click', () => {
      modal ??= new RankingModal(new RankingIdentityStore());
      void modal.open({
        date: today.dateKey,
        region: today.regionId,
        regionName: today.regionName,
        shortDate: today.shortDate,
      });
    });
    top.push(rankBtn);
  }
  if (lastSummary) top.push(buildContinue(lastSummary, medalOfStage(lastSummary.id)));
  const mapSection = el('section', 'landing-map');
  const legend = el('p', 'landing-map__legend');
  const medalCounts = { bronze: 0, silver: 0, gold: 0, master: 0 };
  for (const summary of summaries) {
    const m = medalOfStage(summary.id);
    if (m !== 'none') medalCounts[m] += 1;
  }
  legend.textContent =
    `メダル：銅 ${medalCounts.bronze}・銀 ${medalCounts.silver}・金 ${medalCounts.gold}・白金 ${medalCounts.master}` +
    '（あそんだ県は明るく、メダルの県はその色。白金＝おはしもコンティニューも使わずに最上位）';
  mapSection.append(buildTileMap(summaries, medalOfStage), legend);
  top.push(mapSection);

  const list = el('div', 'landing__list');
  for (const group of groupByArea(summaries)) {
    const heading = el('div', 'landing-area');
    const played = group.items.filter(hasPlayed).length;
    heading.append(
      el('span', 'landing-area__name', group.name),
      el('span', 'landing-area__count', `${played} / ${group.items.length}`),
    );
    list.append(heading, ...group.items.map((s) => buildRow(s, medalOfStage(s.id))));
  }

  const foot = el('footer', 'landing__foot');
  foot.append(
    el(
      'p',
      'landing__note',
      '地域ごとに、図鑑とハイスコアは別々に記録されます。音量などの設定は共通です。',
    ),
  );
  const links = el('p', 'landing__note landing__links');
  const privacy = el('a', undefined, 'プライバシーポリシー');
  privacy.href = '/privacy.html';
  const support = el('a', undefined, 'サポート');
  support.href = '/support.html';
  const guidelines = el('a', undefined, '配信・動画投稿について');
  guidelines.href = '/guidelines.html';
  links.append(privacy, ' ・ ', support, ' ・ ', guidelines);
  foot.append(links);
  // 音源のクレジット（CC BY の素材を使ったときは表示が条件になる）
  if (AUDIO_CREDITS.length > 0) {
    const credits = el('p', 'landing__note landing__credits');
    AUDIO_CREDITS.forEach((credit, i) => {
      if (i > 0) credits.append('　');
      credits.append(`${credit.role}：`);
      const link = el('a', undefined, credit.name);
      link.href = credit.url;
      link.target = '_blank';
      link.rel = 'noopener';
      credits.append(link);
    });
    foot.append(credits);
  }

  // 行ったことのある県の印（端末に保存。トロフィーには数えない）
  const visited = new VisitedStore();
  const visitedCount = el('p', 'landing-visited-count');
  const syncVisited = () => {
    markVisitedTiles(mapSection, visited);
    visitedCount.textContent = `行ったことのある県：${visited.count}（下の一覧の「☐ 行った」で付け外しできます）`;
  };
  mapSection.append(visitedCount);
  decorateVisitedRows(list, '.landing-item', visited, syncVisited);
  syncVisited();

  const listHead = el('p', 'landing__listhead', '地方から選ぶ');
  screen.append(head, bar, ...top, listHead, list, foot);
  appRoot.replaceChildren(screen);
  appRoot.dataset.view = 'landing';
}
